<div class="residence-dialog-container">
  <!-- Loading Overlay (only for residence history updates) -->
  @if (!data.isContactInfoDialog && isProcessing) {
    <div class="loading-overlay">
      <div class="loading-content">
        <mat-spinner diameter="60"></mat-spinner>
        <p class="loading-text">Updating residence history...</p>
      </div>
    </div>
  }

  <!-- STAGE 1: Loading - Updating Profile -->
  @if (showLoading) {
    <div class="profile-updating-container">
      <div class="updating-content">
        <mat-spinner diameter="60" class="updating-spinner"></mat-spinner>
        <h2 class="updating-title">Updating Your Profile</h2>
        <p class="updating-text">We're saving your information from the license...</p>
      </div>
    </div>
  }

  <!-- STAGE 2: Residence History Question -->
  @if (showResidenceQuestion) {
    <div class="dialog-header">
      <h1 mat-dialog-title class="dialog-title">
        Update Residence History?
      </h1>
    </div>

    <div mat-dialog-content class="dialog-content">
      <div class="intro-text-simple">
        We found an address on your license:
      </div>

      <!-- Address Box -->
      <div class="address-box new">
        <div class="address-label">
          <mat-icon class="label-icon">place</mat-icon>
          <span>Address from License</span>
        </div>
        <div class="address-content">
          {{ data.address }}
        </div>
      </div>

      <div class="question-text">
        Would you like to add this to your residence history?
      </div>
    </div>

    <div mat-dialog-actions class="dialog-actions">
      <button
        mat-stroked-button
        class="cancel-button"
        (click)="onCancel()"
        data-test="cancel-button"
      >
        Not Now
      </button>
      <button
        mat-flat-button
        color="primary"
        class="confirm-button"
        (click)="onConfirm()"
        data-test="confirm-button"
        cdkFocusInitial
      >
        Yes, Add to History
      </button>
    </div>
  }

  <!-- STAGE 3: Comprehensive Success Message (For New Licenses) -->
  @if (showSuccessMessage) {
    <div class="success-container">
      <div class="success-content">
        <mat-icon class="success-icon">check_circle</mat-icon>
        <h2 class="success-title">All information has been saved to your profile.</h2>
        
        <div class="success-details">
          @if (data.dobUpdated) {
            <div class="success-item">
              <mat-icon class="item-icon">check</mat-icon>
              <span>Date of Birth updated</span>
            </div>
          }
          @if (data.addressUpdated) {
            <div class="success-item">
              <mat-icon class="item-icon">check</mat-icon>
              <span>Address updated</span>
            </div>
          }
          @if (data.residenceHistoryUpdated) {
            <div class="success-item">
              <mat-icon class="item-icon">check</mat-icon>
              <span>Residence History updated</span>
            </div>
          }
        </div>

        <p class="redirect-text">Redirecting to your profile...</p>
      </div>
    </div>
  }

  <!-- ORIGINAL DIALOGS (Profile Update, Contact Info, Residence History) -->
  @if (!showLoading && !showResidenceQuestion && !showSuccessMessage) {
    <div class="dialog-header">
      <h1 mat-dialog-title class="dialog-title">
        @if (data.isProfileUpdateDialog) {
          <mat-icon class="title-icon success">check_circle</mat-icon>
          Profile Updated
        } @else if (data.isContactInfoDialog) {
          <mat-icon class="title-icon">place</mat-icon>
          Update Contact Address?
        } @else {
          Update Residence History?
        }
      </h1>
    </div>

    <div mat-dialog-content class="dialog-content">
      @if (data.isProfileUpdateDialog) {
        <!-- Profile Auto-Update Success Dialog -->
        <div class="intro-text-simple">
          We've updated your profile using information from your license:
        </div>

        @if (data.dobUpdated) {
          <div class="profile-update-section">
            <div class="section-label">
              <mat-icon class="label-icon">cake</mat-icon>
              <span>Date of Birth</span>
            </div>
            <div class="section-value">
              {{ formatDateForDisplay(data.dateOfBirth) }}
            </div>
          </div>
        }

        @if (data.addressUpdated) {
          <div class="profile-update-section">
            <div class="section-label">
              <mat-icon class="label-icon">place</mat-icon>
              <span>Address</span>
            </div>
            <div class="section-value">
              {{ data.address }}
            </div>
          </div>
        }

        <div class="info-notice">
          <mat-icon class="info-icon">info</mat-icon>
          <span>You can update these details anytime from your profile page.</span>
        </div>
      } @else if (data.isContactInfoDialog) {
        <!-- Simplified Contact Info Dialog (Option 2) -->
        <div class="intro-text-simple">
          Your license address:
        </div>

        <!-- New Address Box (Compact) -->
        <div class="address-box compact">
          <div class="address-content">
            {{ data.newAddress }}
          </div>
        </div>

        <div class="question-text">
          Update your contact info?
        </div>
      } @else {
        <!-- Full Residence History Dialog -->
        <div class="intro-text">
          We found a new address on your license. Would you like to update your residence history?
        </div>

        <!-- Date of Birth Mismatch Warning (if applicable) -->
        @if (data.extractedDateOfBirth && data.profileDateOfBirth && data.extractedDateOfBirth !== data.profileDateOfBirth) {
          <div class="dob-warning">
            <mat-icon class="warning-icon">warning</mat-icon>
            <div class="warning-content">
              <strong>Date of Birth Mismatch Detected</strong>
              <div class="dob-details">
                <div class="dob-row">
                  <span class="dob-label">License DOB:</span>
                  <span class="dob-value">{{ data.extractedDateOfBirth }}</span>
                </div>
                <div class="dob-row">
                  <span class="dob-label">Profile DOB:</span>
                  <span class="dob-value">{{ data.profileDateOfBirth }}</span>
                </div>
              </div>
              <div class="dob-note">
                Please verify your date of birth. If incorrect, contact support to update your profile.
              </div>
            </div>
          </div>
        }

        <!-- Current Address Box -->
        <div class="address-box current">
          <div class="address-label">
            <mat-icon class="label-icon">home</mat-icon>
            <span>Current Residence</span>
          </div>
          <div class="address-content">
            {{ data.currentAddress }}
          </div>
        </div>

        <!-- Arrow Separator -->
        <div class="address-separator">
          <mat-icon>arrow_downward</mat-icon>
        </div>

        <!-- New Address Box -->
        <div class="address-box new">
          <div class="address-label">
            <mat-icon class="label-icon">new_releases</mat-icon>
            <span>New Address from License</span>
          </div>
          <div class="address-content">
            {{ data.newAddress }}
          </div>
        </div>
      }
    </div>

    <div mat-dialog-actions class="dialog-actions">
      @if (data.isProfileUpdateDialog) {
        <button
          mat-flat-button
          color="primary"
          class="confirm-button full-width"
          (click)="onConfirm()"
          cdkFocusInitial
        >
          Go to Profile
        </button>
      } @else {
        <button
          mat-stroked-button
          class="cancel-button"
          (click)="onCancel()"
          [disabled]="isProcessing"
          data-test="cancel-button"
        >
          {{ data.isContactInfoDialog ? 'Not Now' : 'No, Keep Existing' }}
        </button>
        <button
          mat-flat-button
          color="primary"
          class="confirm-button"
          (click)="onConfirm()"
          [disabled]="isProcessing"
          data-test="confirm-button"
          cdkFocusInitial
        >
          {{ data.isContactInfoDialog ? 'Go to Profile' : 'Yes, Update' }}
        </button>
      }
    </div>
  }
</div>

